import React from 'react';
import { useLocation } from 'react-router-dom';

import { useWaitlistSignup } from '../useWaitlistSignup.ts';

import styles from './WaitlistBand.module.css';

/** Props for {@link WaitlistBand}. */
export interface WaitlistBandProps {
  /** Section heading. Keep the canonical CTA string unless a deck overrides it. */
  readonly title?: string;
  /** Short paragraph under the heading. */
  readonly lede?: React.ReactNode;
  /** Benefit chips shown above the form. */
  readonly bullets?: readonly string[];
}

const DEFAULT_BULLETS: readonly string[] = [
  'Early-access priority when onboarding opens',
  'Build updates as capabilities ship',
  'Roadmap input while the product is being shaped',
];

/**
 * Full-width closing waitlist CTA with `id="waitlist"` — rendered once per
 * page by {@link MarketingLayout}. Pages never render this themselves.
 *
 * @remarks
 * Reuses {@link useWaitlistSignup} (the exact logic extracted from the
 * coming-soon page's EmailCapture): `VITE_WAITLIST_API_URL` fetch, graceful
 * degradation when unset, and the `trackWaitlistSignup` conversion event.
 * The signup source is attributed per page as `marketing:<pathname>`.
 */
export function WaitlistBand({
  title = 'Join the waitlist',
  lede,
  bullets = DEFAULT_BULLETS,
}: WaitlistBandProps): React.ReactElement {
  const { pathname } = useLocation();
  const { email, setEmail, status, errorMessage, submit } = useWaitlistSignup(
    `marketing:${pathname}`
  );

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    void submit();
  }

  return (
    <section
      id='waitlist'
      className={styles['band']}
      aria-labelledby='waitlist-title'
    >
      <div className={styles['inner']}>
        <p className={styles['eyebrow']}>Pre-launch</p>
        <h2 id='waitlist-title' className={styles['title']}>
          {title}
        </h2>
        <div className={styles['lede']}>
          {lede ??
            'plcy.io is in build, and the people on the waitlist are shaping it.'}
        </div>
        {bullets.length > 0 && (
          <ul className={styles['bullets']}>
            {bullets.map((bullet) => (
              <li key={bullet} className={styles['bullet']}>
                {bullet}
              </li>
            ))}
          </ul>
        )}
        {status === 'success' ? (
          <p className={styles['success']}>You&apos;re on the list.</p>
        ) : (
          <>
            <form className={styles['form']} onSubmit={handleSubmit}>
              <input
                className={styles['input']}
                type='email'
                placeholder='Enter your email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === 'loading'}
                required
                aria-label='Email address'
                data-lpignore='true'
                data-1p-ignore
              />
              <button
                className={styles['button']}
                type='submit'
                disabled={status === 'loading'}
              >
                {status === 'loading' ? '…' : 'Join the waitlist'}
              </button>
            </form>
            <div aria-live='polite'>
              {status === 'error' && (
                <p className={styles['error']}>{errorMessage}</p>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
